import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface CupomFiscalItem {
    idNfce: number
    idPedido: number | null
    dataSolicitacao: string | null
    dataRealizacao: string | null
    valorTotal: number | null
    status: string
    mensagem: string | null
    numeroNota: number | null
    identificador: string | null
    pdf: string | null
    nomeCliente: string | null
}

export interface ItemRelatorioFiscal {
    identificadorCupom: string
    id: string
    descricao: string
    ncm: string
    cfop: string
    cst: string
    quantidade: number
    vendidoPor: number
    percentualPis: number
    valorPis: number
    percentualCofins: number
    valorCofins: number
}

export interface ItemRelatorioCompras {
    numero: string
    serie: string
    mod: string
    emissao: string
    cfop: string
    fornecedor: string
    total: number
    icms: number
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }
type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

export const obterCuponsFiscais = async function (
    authorization: string,
    params: {
        dataInicio?: string
        dataFim?: string
        identificador?: string
        numeroInicio?: string
        numeroFim?: string
        tipo?: string
    }
): Promise<CupomFiscalItem[] | ErroApi> {
    try {
        const queryParams: Record<string, string> = {}
        if (params.dataInicio) queryParams.dataInicio = params.dataInicio
        if (params.dataFim) queryParams.dataFim = params.dataFim
        if (params.identificador) queryParams.identificador = params.identificador
        if (params.numeroInicio) queryParams.numeroInicio = params.numeroInicio
        if (params.numeroFim) queryParams.numeroFim = params.numeroFim
        if (params.tipo) queryParams.tipo = params.tipo

        const response = await api
            .get<CupomFiscalItem[] | RespostaErroRaw>("notafiscal/ObterCuponsFiscais", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
                params: queryParams,
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as CupomFiscalItem[]
    } catch (error) {
        RegistrarLogErro(error, "ObterCuponsFiscais", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const sincronizarCuponsFornecedor = async function (
    authorization: string,
    dataInicio: string,
    dataFim: string
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(
                "notafiscal/SincronizarCuponsFornecedor",
                { dataInicio, dataFim },
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "SincronizarCuponsFornecedor")
    } catch (error) {
        RegistrarLogErro(error, "SincronizarCuponsFornecedor", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const atualizarPendentesFornecedor = async function (
    authorization: string,
    dataInicio: string,
    dataFim: string
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(
                "notafiscal/AtualizarPendentesFornecedor",
                { dataInicio, dataFim },
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "AtualizarPendentesFornecedor")
    } catch (error) {
        RegistrarLogErro(error, "AtualizarPendentesFornecedor", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const reimprimirCupomFiscal = async function (
    authorization: string,
    idNfce: number
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(
                `notafiscal/ReimprimirCupomFiscal/${idNfce}`,
                null,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "ReimprimirCupomFiscal")
    } catch (error) {
        RegistrarLogErro(error, "ReimprimirCupomFiscal", String(idNfce))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const cancelarCupomFiscal = async function (
    authorization: string,
    idNfce: number
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(
                `notafiscal/CancelarCupomFiscal/${idNfce}`,
                null,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "CancelarCupomFiscal")
    } catch (error) {
        RegistrarLogErro(error, "CancelarCupomFiscal", String(idNfce))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const baixarContabilidade = async function (
    authorization: string,
    mes: number,
    ano: number,
    email?: string
): Promise<Blob | { mensagem: string } | ErroApi> {
    try {
        const params: Record<string, string | number> = { mes, ano }
        if (email) params.email = email

        const response = await api
            .get("notafiscal/BaixarContabilidade", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
                params,
                responseType: email ? "json" : "blob",
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        if (email) {
            return response.data as { mensagem: string }
        }
        return response.data as Blob
    } catch (error) {
        RegistrarLogErro(error, "BaixarContabilidade", `mes=${mes} ano=${ano}`)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const relatorioFiscalVendas = async function (
    authorization: string,
    dataInicio: string,
    dataFim: string
): Promise<ItemRelatorioFiscal[] | ErroApi> {
    try {
        const response = await api
            .get<ItemRelatorioFiscal[] | RespostaErroRaw>("notafiscal/RelatorioFiscalVendas", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
                params: { dataInicio, dataFim },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as ItemRelatorioFiscal[]
    } catch (error) {
        RegistrarLogErro(error, "RelatorioFiscalVendas", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const relatorioCompras = async function (
    authorization: string,
    dataInicio: string,
    dataFim: string
): Promise<ItemRelatorioCompras[] | ErroApi> {
    try {
        const response = await api
            .get<ItemRelatorioCompras[] | RespostaErroRaw>("notafiscal/RelatorioCompras", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
                params: { dataInicio, dataFim },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return response.data as ItemRelatorioCompras[]
    } catch (error) {
        RegistrarLogErro(error, "RelatorioCompras", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
